import { useRef, useState } from "react"
import { useNavigate, useLocation } from "react-router"
import { Button, ButtonGroup, Td, useToast } from "@chakra-ui/react"
import { Container, Form, Heading, Icon, IconButton, Row, Searchable, Table, Tag } from "components"
import { Enums, API } from "config"
import { useInvoicesList } from "hooks"

export default function Invoices() {
    const navigate  = useNavigate()
    const location  = useLocation()
    const toast     = useToast()
    const xmlRef    = useRef()
    const [page, setPage]         = useState(1)
    const [search, setSearch]     = useState(location.state?.search ?? '')
    const [status, setStatus]     = useState('')
    const [importing, setImporting] = useState(false)

    const invoices = useInvoicesList(page, { status, search })

    const columns = [
        { children: 'Número' },
        { children: 'Fornecedor' },
        { children: 'Núcleo' },
        { children: 'Status' },
        { children: 'Data', isNumeric: true },
        { children: '' },
    ]

    const statusOptions = [
        { label: 'Todos os status',       value: '' },
        { label: 'Aguardando Carga',      value: Enums.InvoiceStatus.ARRIVING           },
        { label: 'Parc. Recebida',        value: Enums.InvoiceStatus.PARTIALLY_RECEIVED },
        { label: 'Totalmente Recebida',   value: Enums.InvoiceStatus.FULLY_RECEIVED     },
        { label: 'Rejeitada',             value: Enums.InvoiceStatus.REJECTED           },
        { label: 'Cancelada',             value: Enums.InvoiceStatus.CANCELED           },
        { label: 'Provisória',            value: Enums.InvoiceStatus.PROVISIONAL        },
    ]

    const handleDocImport = async (file) => {
        if (!file) return
        setImporting(true)
        try {
            const fd = new FormData()
            fd.append('file', file)
            const res = await API.post('/invoices/parse-nfe', fd)
            navigate('/stock/invoices/new', {
                state: {
                    parsed:    res.data.parsed,
                    source:    res.data.source,   // 'xml' | 'pdf'
                    message:   res.data.message,
                    inputFile: file,              // usado como evidência pré-anexada
                },
            })
        } catch (e) {
            toast({
                title: e?.response?.data?.message ?? 'Erro ao processar o arquivo.',
                status: 'error', duration: 6000,
            })
        } finally {
            setImporting(false)
            xmlRef.current.value = ''
        }
    }

    return (
        <Container maxW="90%" mt={10}>
            <Heading title="NF-e / Entradas">
                <ButtonGroup size="sm" isDisabled={invoices.isFetching || importing}>
                    <IconButton icon="refresh" isLoading={invoices.isFetching} onClick={invoices.refetch} />
                    <Button leftIcon={<Icon name="upload" />} colorScheme="purple" isLoading={importing}
                        loadingText="Processando..."
                        onClick={() => xmlRef.current?.click()}>
                        Importar XML / PDF
                    </Button>
                    <Button leftIcon={<Icon name="new" />} colorScheme="teal"
                        onClick={() => navigate('/stock/invoices/new')}>
                        Nova NF-e
                    </Button>
                    <Button leftIcon={<Icon name="stock" />} colorScheme="purple" variant="outline"
                        onClick={() => navigate('/stock/invoices/provisional')}>
                        Entrada sem nota
                    </Button>
                </ButtonGroup>
            </Heading>

            {/* Input oculto para selecionar XML */}
            <input
                ref={xmlRef} type="file" accept=".xml,.pdf" style={{ display: 'none' }}
                onChange={e => handleDocImport(e.target.files?.[0])}
            />

            <Searchable value={search} setValue={v => { setSearch(v); setPage(1) }}
                placeholder="Buscar por número ou fornecedor...">
                <Form.Select onChange={e => { setStatus(e.target.value); setPage(1) }} options={statusOptions} />
            </Searchable>

            <Table columns={columns} mt={4} setPage={setPage} meta={invoices.data?.meta}>
                {invoices.data?.data?.map(item => (
                    <InvoiceRow key={item.id} data={item}
                        onClick={() => navigate(`/stock/invoices/${item.id}`)} />
                ))}
            </Table>
        </Container>
    )
}

const InvoiceRow = ({ data, onClick }) => {
    const status = Enums.InvoiceStatus.data(data?.status)
    return (
        <Row onClick={onClick} cursor="pointer">
            <Td>
                <span style={{ fontWeight: 700, fontFamily: data?.is_provisional ? 'monospace' : undefined, color: data?.is_provisional ? 'var(--chakra-colors-purple-500)' : undefined }}>
                    {data?.number}
                </span>
                {data?.is_provisional && (
                    <Tag colorScheme="purple" size="sm" ml={2}>Provisória</Tag>
                )}
            </Td>
            <Td>{data?.supplier_name}</Td>
            <Td>{data?.distributor?.name}</Td>
            <Td><Tag colorScheme={status.colorScheme}>{status.label}</Tag></Td>
            <Td isNumeric>{new Date(data?.created_at).toLocaleDateString('pt-BR')}</Td>
            <Td isNumeric><Icon name="right" /></Td>
        </Row>
    )
}
